import { useCallback, useEffect, useState } from 'react';

import classNames from 'classnames';
import { motion, useAnimationControls } from 'framer-motion';

import type { BingoEventType } from '../../../../../stores/bingo/types';
import {
  BINGO_BOARD_LEFT_CASUAL,
  BINGO_BOARD_LEFT_SWEEPS,
  BINGO_BOARD_RIGHT_CASUAL,
  BINGO_BOARD_RIGHT_SWEEPS,
  BINGO_LOGO_BG_CASUAL,
  BINGO_LOGO_BG_SWEEPS,
  BINGO_TEXT,
} from '../../../constants';
import type { AnimationReadyRef } from '../../../hooks/useBingoWidgetData';
import type { WidgetSizeType } from '../../../types';

import styles from './BingoWidgetLogo.module.scss';

const STAGGER = 0.1;
const DURATION = 0.4;
const ANIM = {
  duration: DURATION,
  ease: 'easeInOut' as const,
  times: [0, 0.4, 1],
};

const getContainerStyle = (sizeType: WidgetSizeType, timerIncluded?: boolean) => {
  if (sizeType === 'wide') {
    return styles.containerWide;
  }
  if (sizeType === 'small') {
    return timerIncluded ? styles.containerWithTimer : styles.containerWithoutTimer;
  }
  return '';
};

const getLeftBoardImgStyle = (sizeType: WidgetSizeType, timerIncluded?: boolean) => {
  if (sizeType === 'wide') {
    return styles.leftBoardImgWide;
  }
  if (sizeType === 'small') {
    return timerIncluded ? styles.leftBoardImgWithTimer : styles.leftBoardImgWithoutTimer;
  }
  return '';
};

const getRightBoardImgStyle = (sizeType: WidgetSizeType, timerIncluded?: boolean) => {
  if (sizeType === 'wide') {
    return styles.rightBoardImgWide;
  }
  if (sizeType === 'small') {
    return timerIncluded ? styles.rightBoardImgWithTimer : styles.rightBoardImgWithoutTimer;
  }
  return '';
};

const getTextImgStyle = (sizeType: WidgetSizeType, timerIncluded?: boolean) => {
  if (sizeType === 'wide') {
    return styles.textImgWide;
  }
  if (sizeType === 'small') {
    return timerIncluded ? styles.textImgWithTimer : styles.textImgWithoutTimer;
  }
  return '';
};

interface BingoWidgetLogoProps {
  sizeType: WidgetSizeType;
  bingoType: BingoEventType;
  timerIncluded?: boolean;
  rotation?: boolean;
  animationReadyRef?: AnimationReadyRef;
}

export function BingoWidgetLogo(props: BingoWidgetLogoProps) {
  const { sizeType, timerIncluded, rotation, bingoType, animationReadyRef } = props;
  const [currentBingoType, setCurrentBingoType] = useState<BingoEventType>(bingoType);
  const containerStyle = getContainerStyle(sizeType, timerIncluded);
  const leftBoardImgStyle = getLeftBoardImgStyle(sizeType, timerIncluded);
  const rightBoardImgStyle = getRightBoardImgStyle(sizeType, timerIncluded);
  const textImgStyle = getTextImgStyle(sizeType, timerIncluded);

  const textImgControls = useAnimationControls();
  const leftImgControls = useAnimationControls();
  const rightImgControls = useAnimationControls();
  const bgImgControls = useAnimationControls();

  const disappear = useCallback(() => {
    void textImgControls.start({ scale: [1, 1.1, 0], transition: ANIM });
    void leftImgControls.start({ scale: [1, 1.1, 0], transition: { ...ANIM, delay: STAGGER * 2 } });
    void rightImgControls.start({ scale: [1, 1.1, 0], transition: { ...ANIM, delay: STAGGER * 3 } });
    return bgImgControls.start({ scale: [1, 1.1, 0], transition: { ...ANIM, delay: STAGGER * 4 } });
  }, [bgImgControls, leftImgControls, rightImgControls, textImgControls]);

  const appear = useCallback(() => {
    void bgImgControls.start({ scale: [0, 1.1, 1], transition: ANIM });
    void rightImgControls.start({ scale: [0, 1.1, 1], transition: { ...ANIM, delay: STAGGER * 2 } });
    void leftImgControls.start({ scale: [0, 1.1, 1], transition: { ...ANIM, delay: STAGGER * 3 } });
    return textImgControls.start({ scale: [0, 1.1, 1], transition: { ...ANIM, delay: STAGGER * 4 } });
  }, [bgImgControls, leftImgControls, rightImgControls, textImgControls]);

  const rotate = useCallback(
    async (nextBingoType: BingoEventType) => {
      await disappear();
      setCurrentBingoType(nextBingoType);
      await appear();
    },
    [appear, disappear],
  );

  useEffect(() => {
    if (!animationReadyRef?.current) {
      return;
    }
    if (rotation) {
      void rotate(bingoType);
    }
  }, [animationReadyRef, bingoType, rotate, rotation]);

  return (
    <div className={classNames(styles.container, containerStyle)}>
      <motion.img
        alt="Bingo logo"
        animate={bgImgControls}
        className={styles.bgImg}
        initial={{ scale: 1 }}
        src={currentBingoType === 'CASUAL' ? BINGO_LOGO_BG_CASUAL : BINGO_LOGO_BG_SWEEPS}
      />
      <motion.img
        alt="Bingo left board"
        animate={leftImgControls}
        className={classNames(styles.leftBoardImg, leftBoardImgStyle)}
        initial={{ scale: 1 }}
        src={currentBingoType === 'CASUAL' ? BINGO_BOARD_LEFT_CASUAL : BINGO_BOARD_LEFT_SWEEPS}
      />
      <motion.img
        alt="Bingo right board"
        animate={rightImgControls}
        className={classNames(styles.rightBoardImg, rightBoardImgStyle)}
        initial={{ scale: 1 }}
        src={currentBingoType === 'CASUAL' ? BINGO_BOARD_RIGHT_CASUAL : BINGO_BOARD_RIGHT_SWEEPS}
      />
      <motion.img
        alt="Bingo text"
        animate={textImgControls}
        className={classNames(styles.textImg, textImgStyle)}
        initial={{ scale: 1 }}
        src={BINGO_TEXT}
      />
    </div>
  );
}
